import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Loader, X, Archive } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { User } from "types/user.type";
import { useMutation, useQueryClient } from "react-query";
import { archiveUser } from "services/user.service";
import DeleteModal from "Common/DeleteModal";
import useArchivedUsers from "hooks/useArchivedUser";

const KycBadge = ({ status }: { status: string }) => {
  if (status === "Approved")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
        <CheckCircle size={11} /> Approved
      </span>
    );
  if (status === "In Review")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
        <Loader size={11} /> In Review
      </span>
    );
  if (status === "Rejected")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
        <X size={11} /> Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 text-xs font-medium">
      {status}
    </span>
  );
};

const ArchivedUsers = () => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { data, isLoading, onQueryChange } = useArchivedUsers();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { isLoading: archiving, mutate: archive } = useMutation(
    async (input: string) => await archiveUser(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        setShowConfirm(false);
        navigate("/");
      },
      onError(error: any) { toast.error(error?.response?.data?.message); },
    }
  );

  const confirmArchive = () => { if (user) archive(user._id); };

  const viewUser = (u: User) => navigate(`/pages-account/${u._id}`);

  const handlePaginate = (page: number) => onQueryChange("page", page);
  const handleSearch = (val: string) => onQueryChange("search", val);

  return (
    <React.Fragment>
      <BreadCrumb title="Archived Users" pageTitle="Archived Users" />
      <ToastContainer closeButton={false} limit={1} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <input
            onChange={({ target }) => handleSearch(target.value)}
            placeholder="Search by name, role or status…"
            className="w-full sm:max-w-sm px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
        ) : (data?.data?.total ?? 0) < 1 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Archive size={22} className="text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No archived users</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table data-stack className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.data?.data.map((u) => (
                    <tr
                      key={u._id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => viewUser(u)}
                    >
                      <td data-label="Name" data-cell="title" className="px-4 py-3">
                        <p className="font-medium text-gray-900">{u.firstName} {u.lastName}</p>
                        <p className="text-xs text-gray-500">{u.role}</p>
                      </td>
                      <td data-label="Email" className="px-4 py-3 text-gray-600">{u.email}</td>
                      <td data-label="Phone" className="px-4 py-3 text-gray-600">
                        <span className="flex items-center gap-1.5">
                          {u.phoneNumber}
                          {u.isPhoneVerified
                            ? <CheckCircle size={12} className="text-green-500" />
                            : <Loader size={12} className="text-gray-400" />}
                        </span>
                      </td>
                      <td data-label="Joined" className="px-4 py-3 text-gray-500">
                        {new Date(u.createdAt).toLocaleDateString()}{" "}
                        {new Date(u.createdAt).toLocaleTimeString()}
                      </td>
                      <td data-label="Status" className="px-4 py-3">
                        <KycBadge status={u.kycStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3 items-center justify-end py-3 px-4 border-t border-gray-100">
              <button
                disabled={Number(data?.data?.page) <= 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => handlePaginate(Number(data?.data?.page) - 1)}
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {data?.data?.page}</span>
              <button
                disabled={Number(data?.data?.page) >= Number(data?.data?.total) / Number(data?.data?.limit)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => handlePaginate(Number(data?.data?.page) + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      <DeleteModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onDelete={confirmArchive}
        title="Archive user record"
        message="Are you sure you want to archive this user record?"
        actionText={archiving ? "Processing…" : "Yes, Archive It!"}
      />
    </React.Fragment>
  );
};

export default ArchivedUsers;
